import {
  MemoizedSelector,
  createFeatureSelector,
  createSelector,
} from '@ngrx/store';
import {
  DashboardVarValue,
  DashboardVesselData,
  StoreDashboardState,
  TDownsampleChartPoint,
} from '../dashboard.state';
import { SelectedVesselImo, VesselImoSelector } from '@dashboard-store';
import { ExtraStateIds, SystemTags } from '@dashboard/models';

const dataState = createFeatureSelector<StoreDashboardState>('dashboard');

const variableSelector = (
  tagName: SystemTags | ExtraStateIds,
  vesselId: string = null
) =>
  createSelector(
    dataState,
    SelectedVesselImo,
    VesselImoSelector(vesselId),
    (dataState, selectedImo, imo) => {
      const _imo = imo ?? selectedImo;
      if (!_imo) return '';
      return dataState.data[_imo][tagName].formattedValue;
    }
  );

const variablePreviewSelector = (
  tagName: SystemTags | ExtraStateIds,
  vesselId: string = null
) =>
  createSelector(
    dataState,
    SelectedVesselImo,
    VesselImoSelector(vesselId),
    (dataState, selectedImo, imo) => {
      const _imo = imo ?? selectedImo;
      if (_imo) return dataState.data[_imo][tagName];
      return null;
    }
  );

const variableObjectSelector = (
  tagName: SystemTags | ExtraStateIds,
  vesselId: string = null
) =>
  createSelector(
    dataState,
    SelectedVesselImo,
    VesselImoSelector(vesselId),
    (dataState, selectedImo, imo) => {
      const _imo = imo ?? selectedImo;
      if (!_imo) return null;
      const t = dataState.data[_imo][tagName];
      return t;
    }
  );

const MultipleVariableSelector = (
  tagNames: (SystemTags | ExtraStateIds)[],
  vesselId: string = null
) =>
  createSelector(
    dataState,
    SelectedVesselImo,
    VesselImoSelector(vesselId),
    (dataState: StoreDashboardState, selectedImo: string, imo: string) => {
      const _imo = imo ?? selectedImo;
      if (!_imo) return '';
      return tagNames.reduce((prev, tag) => {
        return {
          ...prev,
          [tag]: dataState.data[_imo][tag],
        };
      }, {} as DashboardVesselData);
    }
  ) as MemoizedSelector<StoreDashboardState, DashboardVesselData>;

const DownsampleVariableSelector = (tagName: SystemTags | ExtraStateIds) =>
  createSelector(dataState, SelectedVesselImo, (dataState, imo) => {
    if (!imo) return [];
    return dataState.data[imo][tagName].values as TDownsampleChartPoint[];
  });

const VesselDataSelector = (vesselId: string = null) =>
  createSelector(
    dataState,
    SelectedVesselImo,
    VesselImoSelector(vesselId),
    (dataState, selectedImo, imo) => {
      const _imo = imo ?? selectedImo;
      return dataState.data[_imo];
    }
  );

const ProgressBarSelector = (vesselId: string = null) =>
  createSelector(dataState, SelectedVesselImo, (dataState, imo) => {
    const _imo = vesselId ? dataState.uiState.vessels[vesselId].slug : imo;
    return {
      etd: dataState.data[_imo]?.START_TIMESTAMP?.formattedValue,
      eta: dataState.data[_imo]?.END_TIMESTAMP?.formattedValue,
    };
  });
const TraceSelector = () =>
  createSelector(
    dataState,
    SelectedVesselImo,
    (dataState: StoreDashboardState, activeImo: string) => {
      return activeImo ? dataState.data[activeImo].trace.values : [];
    }
  );

const PositionSelector = (vesselId: string = null) =>
  createSelector(
    dataState,
    variableSelector('COORD', vesselId),
    (state, coord) => {
      if (coord) return coord;
      return null;
    }
  );

const VesselIsStopped = (vesselId: string = null) =>
  createSelector(
    dataState,
    variableSelector('OPERATIONAL_STATUS', vesselId),
    (state, operationalStatus) => {
      const StoppedFlags = ['Stopped', 'N/A'];
      return StoppedFlags.includes(operationalStatus);
    }
  );

/**
 * Returns a list of all coords of vessels
 * @returns
 */
const CoordsSelector = createSelector(dataState, (state) => {
  const coordsPerVessel: { [imo: string]: DashboardVarValue } = {};
  for (const [vesselImo, vesselData] of Object.entries(state.data)) {
    coordsPerVessel[vesselImo] = vesselData['COORD'];
  }
  return coordsPerVessel;
});

export type OperationalStatus = 'Underway' | 'Stopped' | 'N/A';

export type VesselStatusSnapshot = {
  operationalStatus: OperationalStatus;
  inPort: boolean;
  hasError: boolean;
};

// Boolean signals are inconsistently encoded by the backend:
// real WS sends 'True', some mocks send '1', ingestion_error sends 'true'.
const isTruthy = (v: string | undefined): boolean =>
  v === '1' || (v?.toLowerCase() === 'true');

const toOperationalStatus = (v: string | undefined): OperationalStatus => {
  if (v === 'Underway') return 'Underway';
  if (v === 'Stopped')  return 'Stopped';
  return 'N/A';
};

/**
 * Builds a fleet-wide status map keyed by vessel _id.
 *
 * OPERATIONAL_STATUS formattedValue mapping (from backend):
 *   0 → 'N/A'
 *   1 → 'Underway'
 *   2 → 'Stopped'
 *
 * inPort  → IN_PORT_OPERATION truthy ('1' | 'True' | 'true')
 * hasError → ingestion_error truthy (same encoding)
 */
const FleetStatusSelector = createSelector(
  dataState,
  (state: StoreDashboardState): Record<string, VesselStatusSnapshot> => {
    const result: Record<string, VesselStatusSnapshot> = {};
    for (const [vesselId, vessel] of Object.entries(state.uiState.vessels)) {
      const d = state.data[vessel.slug];
      if (!d) continue;
      result[vesselId] = {
        operationalStatus: toOperationalStatus(d['OPERATIONAL_STATUS']?.formattedValue),
        inPort:            isTruthy(d['IN_PORT_OPERATION']?.formattedValue),
        hasError:          isTruthy(d['ingestion_error']?.formattedValue),
      };
    }
    return result;
  }
);

export {
  variableSelector,
  ProgressBarSelector,
  DownsampleVariableSelector,
  VesselDataSelector,
  TraceSelector,
  PositionSelector,
  MultipleVariableSelector,
  VesselIsStopped,
  variableObjectSelector,
  variablePreviewSelector,
  CoordsSelector,
  FleetStatusSelector,
};
